import { ApiModel } from '@/api/api-model';
import { TipoAcuerdo, TipoConsorcio, getTituloEstadoAcuerdoComercial, TiposEstadosAcuerdoComercial, OrigenAcuerdoComercial } from '../views/acuerdo-comercial/acuerdo-comercial.consts';
import { Consorcio } from './Consorcio';
import { Administracion } from './Administracion';
import { formatFecha } from '@/components/vue_filters';
import { AcuerdoComercialConsorcio } from '@/views/acuerdo-comercial/models/AcuerdoComercialConsorcio';

export const API_TYPE_MODULOS_DEFAULT = 'propuesta_comercial';

export class AcuerdoComercial extends ApiModel {

  api_type = API_TYPE_MODULOS_DEFAULT;

  // Atributos a rellenar para el post
  api_types_keys_atributos_request = {
    [API_TYPE_MODULOS_DEFAULT]: [
      //'consorcio_propuesta_libre' [acuerdo_libre - no esta en uso actualmente],
      'tipo_propuesta',
      'tipo_producto',
      'observaciones',
      'setear_default',
      'solo_comunicacion',
      'minimo_ufs',
      'descuentos_ufs',
      'periodo_inicio_facturacion',
      'plantilla',
    ],
  }

  // Relaciones
  api_types_keys_relaciones_request= {
    [API_TYPE_MODULOS_DEFAULT]: [
      'administracion',
      'consorcios',
    ]
  }

  //consorcio_propuesta_libre?: string; [acuerdo_libre - no esta en uso actualmente]
  //codigo?:string; [acuerdo_libre - no esta en uso actualmente]

  // Atributos utilizados para la vista de historial del acuerdo comercial
  historial_eventos?: [
    {
      evento: string,
      fecha: Date,
      usuario: {
        tipo: string,
        nombre: string,
        mail: string
      }
      rechazo_detalles?: string
      dias_extendidos?: string
    }
  ]

  tipo_propuesta: TipoAcuerdo;
  tipo_producto?: TipoConsorcio;
  observaciones?:string;
  plantilla?: string;
  usuario_sales?: string; // No seteado en caso que venga por +consorcios con precio de lista (se crea automáticamente)
  fecha_creacion: Date;
  usuario_edicion?: string;
  fecha_edicion?: Date;
  minimo_ufs?: boolean;

  estado?: TiposEstadosAcuerdoComercial;
  rechazo_detalles?: string;
  rechazo_fecha?: Date;

  solo_comunicacion?: boolean;
  periodo_inicio_facturacion?: string;

  descuentos_ufs?: {
    tipos: {
      general?: number, // Se aplica a todas las UFs facturables
      cochera?: number
    },
    periodos: number[],
  };

  consorcio?: Consorcio;
  propuesta_consorcio?: AcuerdoComercialConsorcio;

  administracion?: Administracion;
  consorcios?: Consorcio[];

  origen?: string;
  fecha_cambio_estado_actual: Date;
  expirada: boolean;
  fecha_expiracion?: Date;

  acciones_posibles?: string[]; // Acciones que se pueden realizar sobre el acuerdo comercial

  /**
   * Inicializo todos los atributos
   */
  constructor(){
    super();
    //this.consorcio_propuesta_libre = null; [acuerdo_libre - no esta en uso actualmente]
    this.plantilla = null;
    this.fecha_creacion = null;
    this.minimo_ufs = false;
    this.periodo_inicio_facturacion = null;
    this.descuentos_ufs = null;
    this.observaciones = null;
  }

  /**
   * Retorna la fecha de creación formateado a 'dd/MM/yyyy'
   */
  get fecha_creacion_formateado() {
    return formatFecha(this.fecha_creacion, 'dd/MM/yyyy');
  }

  /**
   * Retorna la fecha de edición formateada a 'dd/MM/yyyy'
   */
  get fecha_edicion_formateada() {
    return formatFecha(this.fecha_edicion, 'dd/MM/yyyy');
  }

  /**
   * Retorna el estado del acuerdo comercial formateado
   */
  get nombre_estado_formateado(){
    return getTituloEstadoAcuerdoComercial(this.estado);
  }

  /**
   * Copy custom, para rellenar un acuerdo con otro utilizado como modelo
   * 
   * @param acuerdo_original 
   * @param acuerdo_modelo 
   */
  rellenarConAcuerdo(acuerdo_modelo){
    if (!this.tipo_producto){ // Si no tiene ya seteado tipo de producto 
      this.tipo_producto =  this.tipo_propuesta !== TipoAcuerdo.ADMINISTRACION ? acuerdo_modelo.tipo_producto : null;
    }

    // Si no tiene descripción uso la de la plantilla (si es que tiene)
    /* if(!this.consorcio_propuesta_libre){  [acuerdo_libre - no esta en uso actualmente]
      this.consorcio_propuesta_libre = acuerdo_modelo.consorcio_propuesta_libre ?? null;
    } */

    if (this.tipo_propuesta === TipoAcuerdo.LIBRE){
      this.solo_comunicacion = false;
    }

    this.minimo_ufs = acuerdo_modelo.minimo_ufs ?? false;
    this.descuentos_ufs = acuerdo_modelo.descuentos_ufs ?? null;
    this.periodo_inicio_facturacion = acuerdo_modelo.periodo_inicio_facturacion ?? null;
    this.observaciones = acuerdo_modelo.observaciones ?? null;
  }


  /**
   * Computed para devolver el ticket de Jira, contenido en el consorcio.
   * En caso que el consorcio no este seteado, se devuelve null, al igual que en caso que el consorcio NO tenga link asignado.
   */
  get ticketJira(){

    // Para acuerdos libres, el ticket viene desde el acuerdo
    if (this.tipo_propuesta === TipoAcuerdo.LIBRE){
      return this.hasApiLink('ticket_jira') ? this.getApiLink('ticket.jira').href : null;
    }

    // Para el resto de los acuerdos, viene del consorcio (administración no posee ticket)
    if (this.consorcio){
      return this.consorcio.ticketJira;
    }
    return null;
  }

  /**
   * Retorna el origen del acuerdo formateado
   */
  get origen_formateado() {
    switch (this.origen) {
      case OrigenAcuerdoComercial.MAS_CONSORCIOS:
        return '+Consorcios';
      case OrigenAcuerdoComercial.AUTOMATICA:
        return 'Automática';
      case OrigenAcuerdoComercial.MANUAL:
        return 'Manual';
      default:
        return '';
    }
  }

  /**
   * Retorna el descuento formateado en base a:
   * - Si tiene plantilla seteada, se retornará el nombre del mismo
   * - Si tiene descuentos aplicados en las ufs 
   */
  get descuentos_formateados() {

    if(this.plantilla) {
      return this.plantilla;
    }

    return this.descuentos_ufs ? 'Personalizada' : '';
  }
}